import {
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Document, Page } from "react-pdf";
import styled from "styled-components";

import { useWindowSize } from "src/shared/hooks";
import { configurePdfWorker } from "src/shared/utils/pdfWorker";

import "react-pdf/dist/esm/Page/AnnotationLayer.css";

configurePdfWorker();

interface PostEventReportReaderProps {
  /** Short-lived signed URL from HackerAPI, not a bundled asset path. */
  uri: string;
  title: string;
  downloadFilename: string;
  /**
   * Called when the PDF fails to load, which is how an expired signed URL
   * surfaces (pdf.js sees a 403). Returns true if a fresh URL is being
   * fetched, in which case the error state is suppressed.
   */
  onStale?: () => boolean;
}

const MAX_PAGE_WIDTH = 760;
const PAGE_SCROLL_OFFSET = 92;

const PostEventReportReader: React.FC<PostEventReportReaderProps> = ({
  uri,
  title,
  downloadFilename,
  onStale,
}) => {
  const [numPages, setNumPages] = useState(0);
  const [activePage, setActivePage] = useState(1);
  const [pageWidth, setPageWidth] = useState(MAX_PAGE_WIDTH);
  const [loadProgress, setLoadProgress] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  const pagesColumnRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<Array<HTMLElement | null>>([]);
  const { windowWidth } = useWindowSize();

  useLayoutEffect(() => {
    if (!pagesColumnRef.current) return;
    setPageWidth(
      Math.floor(
        Math.min(
          MAX_PAGE_WIDTH,
          pagesColumnRef.current.getBoundingClientRect().width
        )
      )
    );
  }, [windowWidth, numPages]);

  useEffect(() => {
    if (!numPages) return;

    let animationFrame: number | undefined;

    const updateActivePage = () => {
      animationFrame = undefined;

      const viewportBottom = window.innerHeight;
      let mostVisiblePage = 1;
      let largestVisibleHeight = 0;

      pageRefs.current.forEach((page, index) => {
        if (!page) return;

        const rect = page.getBoundingClientRect();
        const visibleHeight = Math.max(
          0,
          Math.min(rect.bottom, viewportBottom) -
            Math.max(rect.top, PAGE_SCROLL_OFFSET)
        );

        if (visibleHeight > largestVisibleHeight) {
          largestVisibleHeight = visibleHeight;
          mostVisiblePage = index + 1;
        }
      });

      if (largestVisibleHeight > 0) {
        setActivePage((currentPage) =>
          currentPage === mostVisiblePage ? currentPage : mostVisiblePage
        );
      }
    };

    const scheduleActivePageUpdate = () => {
      if (animationFrame === undefined) {
        animationFrame = window.requestAnimationFrame(updateActivePage);
      }
    };

    updateActivePage();
    window.addEventListener("scroll", scheduleActivePageUpdate, {
      passive: true,
    });
    window.addEventListener("resize", scheduleActivePageUpdate);

    return () => {
      window.removeEventListener("scroll", scheduleActivePageUpdate);
      window.removeEventListener("resize", scheduleActivePageUpdate);
      if (animationFrame !== undefined) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, [numPages]);

  const setPageRef = useCallback(
    (pageNumber: number) => (node: HTMLElement | null) => {
      pageRefs.current[pageNumber - 1] = node;
    },
    []
  );

  const goToPage = (pageNumber: number) => {
    const nextPage = Math.min(Math.max(pageNumber, 1), numPages);
    const page = pageRefs.current[nextPage - 1];
    if (!page) return;

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // Chromium can cancel an in-progress smooth scroll when the clicked
    // previous/next control becomes disabled as the active page changes.
    // Release focus before scrolling so navigation always reaches its target.
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }

    window.scrollTo({
      top:
        window.scrollY + page.getBoundingClientRect().top - PAGE_SCROLL_OFFSET,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  const retry = () => {
    setLoadError(false);
    setLoadProgress(0);
    setNumPages(0);
    setActivePage(1);
    pageRefs.current = [];
    setRetryKey((key) => key + 1);
  };

  if (loadError) {
    return (
      <ReaderError role="alert">
        <ErrorTitle>We couldn&apos;t load your report.</ErrorTitle>
        <ErrorCopy>
          Check your connection and try again. You can also download the PDF
          directly.
        </ErrorCopy>
        <ErrorActions>
          <SecondaryAction type="button" onClick={retry}>
            Try again
          </SecondaryAction>
          <PrimaryDownload href={uri} download={downloadFilename}>
            <ArrowDownTrayIcon aria-hidden="true" />
            Download PDF
          </PrimaryDownload>
        </ErrorActions>
      </ReaderError>
    );
  }

  return (
    <ReaderShell aria-label={title}>
      {numPages > 0 && (
        <Toolbar aria-label="Report navigation">
          <ToolbarButton
            type="button"
            onClick={() => goToPage(activePage - 1)}
            disabled={activePage === 1}
            aria-label="Go to previous page"
          >
            <ChevronLeftIcon aria-hidden="true" />
            <ButtonLabel>Previous</ButtonLabel>
          </ToolbarButton>
          <PageStatus aria-live="polite">
            <PagePrefix>Page</PagePrefix>
            <CurrentPage>{activePage}</CurrentPage>
            <PageTotal>of {numPages}</PageTotal>
          </PageStatus>
          <ToolbarActions>
            <ToolbarButton
              type="button"
              onClick={() => goToPage(activePage + 1)}
              disabled={activePage === numPages}
              aria-label="Go to next page"
            >
              <ButtonLabel>Next</ButtonLabel>
              <ChevronRightIcon aria-hidden="true" />
            </ToolbarButton>
            <ToolbarDivider aria-hidden="true" />
            <DownloadLink
              href={uri}
              download={downloadFilename}
              aria-label="Download post-event report PDF"
            >
              <ArrowDownTrayIcon aria-hidden="true" />
              <ButtonLabel>Download</ButtonLabel>
            </DownloadLink>
          </ToolbarActions>
        </Toolbar>
      )}

      <DocumentSurface>
        <Document
          key={retryKey}
          file={uri}
          externalLinkTarget="_blank"
          onLoadProgress={({ loaded, total }) =>
            setLoadProgress(total ? Math.round((loaded / total) * 100) : 0)
          }
          onLoadSuccess={({ numPages: loadedPages }) => {
            setNumPages(loadedPages);
            setLoadProgress(100);
          }}
          onLoadError={() => {
            // An expired signed URL 403s here. Let the page swap in a fresh
            // one before falling back to the error card.
            if (!onStale?.()) setLoadError(true);
          }}
          loading={<DocumentSkeleton progress={loadProgress} />}
        >
          <PagesColumn ref={pagesColumnRef}>
            {Array.from({ length: numPages }, (_, index) => {
              const pageNumber = index + 1;
              return (
                <PageFrame
                  key={pageNumber}
                  ref={setPageRef(pageNumber)}
                  data-page-number={pageNumber}
                  aria-label={`Page ${pageNumber} of ${numPages}`}
                >
                  <Page
                    pageNumber={pageNumber}
                    width={pageWidth}
                    renderTextLayer={false}
                    renderAnnotationLayer
                    onRenderError={() => setLoadError(true)}
                    loading={<PageSkeleton aria-hidden="true" />}
                  />
                </PageFrame>
              );
            })}
          </PagesColumn>
        </Document>
      </DocumentSurface>
    </ReaderShell>
  );
};

const ReaderShell = styled.section`
  width: 100%;
`;

const Toolbar = styled.nav`
  position: sticky;
  top: 16px;
  z-index: 10;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: min(100%, ${MAX_PAGE_WIDTH}px);
  min-height: 58px;
  margin: 0 auto 20px;
  padding: 8px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 10px 30px rgba(14, 52, 96, 0.1);
  backdrop-filter: blur(12px);

  ${({ theme }) => theme.mediaQueries.largeMobile`
    width: calc(100% - 32px);
    padding: 6px;
  `}
`;

const toolbarControlStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-width: 44px;
  min-height: 44px;
  padding: 8px 12px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: #374151;
  font-family: Satoshi, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  text-decoration: none;
  transition: background 150ms ease, color 150ms ease, opacity 150ms ease;

  svg {
    width: 18px;
    height: 18px;
    flex: 0 0 auto;
  }

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #0e3460;
    text-decoration: none;
  }

  &:focus-visible {
    outline: 3px solid rgba(117, 165, 238, 0.45);
    outline-offset: 1px;
  }
`;

const ToolbarButton = styled.button`
  ${toolbarControlStyles}
  justify-self: start;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.35;
  }
`;

const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  justify-self: end;
`;

const ToolbarDivider = styled.span`
  width: 1px;
  height: 24px;
  margin: 0 2px;
  background: ${({ theme }) => theme.globalConstants.color.borderGray};
`;

const DownloadLink = styled.a`
  ${toolbarControlStyles}
  color: ${({ theme }) => theme.globalConstants.color.navySecondary};
`;

const PageStatus = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: center;
  gap: 5px;
  min-width: 76px;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
`;

const CurrentPage = styled.span`
  color: ${({ theme }) => theme.globalConstants.color.textDark};
  font-size: 16px;
  font-weight: 700;
`;

const PagePrefix = styled.span`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 13px;
  font-weight: 500;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `}
`;

const PageTotal = styled.span`
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 13px;
  font-weight: 500;
`;

const ButtonLabel = styled.span`
  ${({ theme }) => theme.mediaQueries.largeMobile`
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  `}
`;

const DocumentSurface = styled.div`
  width: 100%;
  padding: 32px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 18px;
  background: #eef2f5;

  ${({ theme }) => theme.mediaQueries.tablet`
    padding: 24px;
    border-radius: 14px;
  `}

  ${({ theme }) => theme.mediaQueries.largeMobile`
    padding: 12px;
    border-right: 0;
    border-left: 0;
    border-radius: 0;
  `}
`;

const PagesColumn = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${MAX_PAGE_WIDTH}px;
  margin: 0 auto;
  gap: 28px;
`;

const PageFrame = styled.section`
  width: 100%;
  scroll-margin-top: ${PAGE_SCROLL_OFFSET}px;
  overflow: hidden;
  border-radius: 8px;
  background: white;
  box-shadow: 0 12px 32px rgba(31, 41, 55, 0.16);

  .react-pdf__Page__canvas {
    display: block;
    width: 100% !important;
    height: auto !important;
  }
`;

const PageSkeleton = styled.div`
  width: 100%;
  aspect-ratio: 8.5 / 11;
  background: linear-gradient(105deg, #f7f8fa 25%, #ffffff 38%, #f7f8fa 52%);
  background-size: 200% 100%;
  animation: report-shimmer 1.4s ease-in-out infinite;

  @keyframes report-shimmer {
    to {
      background-position-x: -200%;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SkeletonWrap = styled.div`
  width: min(100%, ${MAX_PAGE_WIDTH}px);
  margin: 0 auto;
`;

const LoadingLabel = styled.p`
  margin: 18px 0 0;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: 14px;
  font-weight: 600;
  text-align: center;
`;

const DocumentSkeleton: React.FC<{ progress: number }> = ({ progress }) => (
  <SkeletonWrap aria-live="polite" aria-label="Loading post-event report">
    <PageSkeleton aria-hidden="true" />
    <LoadingLabel>
      Loading your report{progress > 0 ? ` - ${progress}%` : "..."}
    </LoadingLabel>
  </SkeletonWrap>
);

const ReaderError = styled.div`
  width: min(100%, 620px);
  margin: 48px auto;
  padding: 40px;
  border: 1px solid ${({ theme }) => theme.globalConstants.color.borderGray};
  border-radius: 16px;
  background: white;
  text-align: center;
  box-shadow: ${({ theme }) => theme.globalConstants.boxShadow.regular};
`;

const ErrorTitle = styled.h2`
  margin: 0 0 10px;
  color: ${({ theme }) => theme.globalConstants.color.textDark};
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.heading};
  font-size: 26px;
`;

const ErrorCopy = styled.p`
  margin: 0 auto;
  max-width: 440px;
  color: ${({ theme }) => theme.globalConstants.color.textSecondary};
  font-size: 15px;
  line-height: 1.6;
`;

const ErrorActions = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;

  ${({ theme }) => theme.mediaQueries.largeMobile`
    flex-direction: column;
  `}
`;

const errorActionStyles = `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 18px;
  border-radius: 8px;
  font-family: Satoshi, sans-serif;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const SecondaryAction = styled.button`
  ${errorActionStyles}
  border: 1px solid #d1d5db;
  background: white;
  color: #374151;
`;

const PrimaryDownload = styled.a`
  ${errorActionStyles}
  border: 1px solid #0e3460;
  background: #0e3460;
  color: white;

  &:hover {
    color: white;
    text-decoration: none;
    background: #0a446d;
  }
`;

export default PostEventReportReader;
